import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useT, type StringKey } from '@/lib/i18n'
import { useEmmaUI } from '@/components/emma/Emma'
import { useChrome } from '@/components/gg/chrome/chromeStore'
import { useSeedBalance, SEED_COSTS } from '@/hooks/useSeeds'
import { useVoiceRing, useVoiceSheet, openVoiceSheet, type RingProgress } from './useVoiceRing'

const DAY = 864e5
const MIN = 6e4
const WEEK = 7 * DAY
const SHOW_MS = 14000
const PAGES_KEY = 'gg.voice.pages'

type Reason = 'build' | 'day' | 'browse'

function line(reason: Reason, p: RingProgress): { key: StringKey; vars?: Record<string, number> } {
  if (p.pct >= 0.75) return { key: 'voice.offerAlmost', vars: { left: p.left } }
  if (reason === 'build') return { key: 'voice.offerBuild' }
  if (reason === 'day') return { key: 'voice.offerDay', vars: { pct: Math.round(p.pct * 100), left: p.left } }
  return { key: 'voice.offerBrowse' }
}

/**
 * Emma's voice offer (Brian's voice-ring.js, Oct 31; Nov 3: "we can have it pop
 * up, they can swipe to ignore for later"). A card with her line and the same
 * count and bar as Profile, Answer one and Later, and a small Stop reminding me.
 * Swiping it away, sideways or down, is Later; a short drag springs back. On a
 * phone it spans the width above the tab bar; on a computer it sits by Emma.
 *
 * When: right after a story is built (the seeds drop by a build's price), on a
 * later day, or a few screens into a visit. Never in the first five minutes
 * after onboarding, never on Record, never over her window, the ring sheet or
 * Record's picker; once a day at most and three times a week. Later snoozes a
 * day; Stop reminding me turns the offers off (the ring stays). She stops when
 * the ring is full. ?voice=nudge shows one now.
 */
export function VoiceOffer({ desktop }: { desktop: boolean }) {
  const t = useT()
  const ring = useVoiceRing()
  const location = useLocation()
  const emmaOpen = useEmmaUI((s) => s.open)
  const sheetOpen = useVoiceSheet((s) => s.open)
  const recPick = useChrome((s) => s.recPick)
  const bars = useChrome((s) => s.bars)
  const { data: balance } = useSeedBalance()
  const [shown, setShown] = useState<Reason | null>(null)
  const hideT = useRef<number | null>(null)
  const ringRef = useRef(ring)
  ringRef.current = ring
  const blockRef = useRef(false)
  blockRef.current = emmaOpen || sheetOpen || recPick || /\/record/.test(location.pathname) || (!desktop && !bars)

  function can(force: boolean): boolean {
    const r = ringRef.current
    if (!r.ready || r.progress.full || !r.progress.total) return false
    if (force) return true
    const n = r.ring.nudge ?? {}, now = Date.now()
    if (n.off || (n.snooze ?? 0) > now || now - (n.last ?? 0) < DAY) return false
    const wk = Math.floor(now / WEEK)
    if (n.wk === wk && (n.count ?? 0) >= 3) return false
    // fresh out of onboarding: let them look around first
    if (r.onboardedAt && now - new Date(r.onboardedAt).getTime() < 5 * MIN) return false
    if (blockRef.current) return false
    return true
  }

  function offer(reason: Reason, force = false): boolean {
    if (!can(force)) return false
    const r = ringRef.current, n = r.ring.nudge ?? {}, now = Date.now(), wk = Math.floor(now / WEEK)
    r.setNudge({ last: now, wk, count: (n.wk === wk ? n.count ?? 0 : 0) + 1, day: new Date().toDateString() })
    setShown(reason)
    if (hideT.current) window.clearTimeout(hideT.current)
    hideT.current = window.setTimeout(() => setShown(null), SHOW_MS)
    return true
  }

  // A few screens into a visit.
  useEffect(() => {
    try { sessionStorage.setItem(PAGES_KEY, String(Number(sessionStorage.getItem(PAGES_KEY) ?? 0) + 1)) } catch { /* private mode */ }
  }, [location.pathname])

  // On arrival: ?voice=nudge, a later day, or a few screens in.
  const started = useRef(false)
  useEffect(() => {
    if (!ring.ready || started.current) return
    started.current = true
    const force = /[?&]voice=nudge/.test(window.location.search)
    const id = window.setTimeout(() => {
      if (force) { offer('browse', true); return }
      const r = ringRef.current, today = new Date().toDateString(), day = r.ring.nudge?.day
      if (day && day !== today && offer('day')) return
      if (day !== today) r.setNudge({ day: today })
      let pages = 0
      try { pages = Number(sessionStorage.getItem(PAGES_KEY) ?? 0) } catch { /* private mode */ }
      if (pages >= 3) offer('browse')
    }, 4500)
    return () => window.clearTimeout(id)
  }, [ring.ready]) // eslint-disable-line react-hooks/exhaustive-deps

  // A few screens in, later in the visit.
  useEffect(() => {
    if (!started.current) return
    let pages = 0
    try { pages = Number(sessionStorage.getItem(PAGES_KEY) ?? 0) } catch { /* private mode */ }
    if (pages >= 3) {
      const id = window.setTimeout(() => offer('browse'), 2500)
      return () => window.clearTimeout(id)
    }
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  // Right after a story is built: the seeds drop by a build's price.
  const lastTotal = useRef<number | null>(null)
  useEffect(() => {
    const tot = balance?.total_available
    if (tot == null) return
    const prev = lastTotal.current
    lastTotal.current = tot
    if (prev != null && prev - tot >= SEED_COSTS.build_story) {
      const id = window.setTimeout(() => offer('build'), 9000)
      return () => window.clearTimeout(id)
    }
  }, [balance?.total_available]) // eslint-disable-line react-hooks/exhaustive-deps

  // Her window, the sheet or Record: the card steps aside.
  useEffect(() => { if (blockRef.current && shown) setShown(null) }, [emmaOpen, sheetOpen, recPick, location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (hideT.current) window.clearTimeout(hideT.current) }, [])

  function hush() { setShown(null); if (hideT.current) window.clearTimeout(hideT.current) }
  function later() { ring.setNudge({ snooze: Date.now() + DAY }); hush() }

  // Swipe it away, sideways or down; a short drag springs back.
  const el = useRef<HTMLDivElement>(null)
  const drag = useRef<{ x0: number; y0: number; dx: number; dy: number; id: number } | null>(null)
  function down(e: React.PointerEvent) {
    if ((e.target as HTMLElement).closest('button')) return
    drag.current = { x0: e.clientX, y0: e.clientY, dx: 0, dy: 0, id: e.pointerId }
    el.current?.classList.add('is-drag')
    try { el.current?.setPointerCapture(e.pointerId) } catch { /* fine */ }
    if (hideT.current) window.clearTimeout(hideT.current)
  }
  function move(e: React.PointerEvent) {
    const d = drag.current
    if (!d || e.pointerId !== d.id || !el.current) return
    d.dx = e.clientX - d.x0; d.dy = Math.max(0, e.clientY - d.y0)
    const far = Math.max(Math.abs(d.dx), d.dy)
    el.current.style.transform = `translate(${d.dx}px, ${d.dy}px) rotate(${d.dx / 30}deg)`
    el.current.style.opacity = String(Math.max(0.2, 1 - far / 240))
  }
  function up(e: React.PointerEvent) {
    const d = drag.current, node = el.current
    if (!d || e.pointerId !== d.id || !node) return
    drag.current = null
    node.classList.remove('is-drag')
    if (Math.abs(d.dx) > 80 || d.dy > 60) {
      node.style.transform = Math.abs(d.dx) > 80 ? `translate(${d.dx > 0 ? 420 : -420}px, ${d.dy}px) rotate(${d.dx > 0 ? 12 : -12}deg)` : 'translate(0, 220px)'
      node.style.opacity = '0'
      later()
      window.setTimeout(() => { node.style.transform = ''; node.style.opacity = '' }, 400)
    } else {
      node.style.transform = ''; node.style.opacity = ''
      hideT.current = window.setTimeout(() => setShown(null), SHOW_MS)
    }
  }

  const p = ring.progress
  const touch = typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches
  const ln = shown ? line(shown, p) : null
  return (
    <div ref={el} className={`vr-offer${desktop ? ' is-desk' : ''}${shown ? ' is-on' : ''}`} role="status" aria-hidden={!shown}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
      {touch && <span className="vr-offer__grab" aria-hidden="true" />}
      <span className="vr-offer__who">Emma</span>
      <p className="vr-offer__t">{ln ? t(ln.key, ln.vars) : ''}</p>
      <div className="vr-offer__pg"><b>{t('voice.cardTitle')}</b><span>{t('voice.left', { left: p.left, total: p.total })}</span></div>
      <div className="vr-bar"><i style={{ width: `${Math.round(p.pct * 100)}%` }} /></div>
      <div className="vr-offer__acts">
        <button type="button" className="vr-btn vr-btn--sm" tabIndex={shown ? 0 : -1} onClick={() => { hush(); openVoiceSheet() }}>{t('voice.answerOne')}</button>
        <button type="button" className="vr-ghost" tabIndex={shown ? 0 : -1} onClick={later}>{t('voice.later')}</button>
      </div>
      <div className="vr-offer__foot">
        <span>{touch ? t('voice.swipeAway') : ''}</span>
        <button type="button" className="vr-off" tabIndex={shown ? 0 : -1} onClick={() => { ring.setNudge({ off: true }); hush() }}>{t('voice.stopReminding')}</button>
      </div>
    </div>
  )
}
